// src/pages/LoginPage.jsx
import { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, Mail, Lock, ChevronsRight } from "lucide-react";
import { motion } from "framer-motion";
import { apiCall } from "@/api/apiCall";
import {
  startSession,
  clearSession,
  getAccessToken,
  isRememberMeEnabled,
  setRememberPreference,
  getRememberedEmail,
  getRememberedPassword,
} from "@/utils/authSession";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/context/PermissionContext";
import CbPortal from "@/assets/img/CbPortal.png";
import mawgiflogo from "@/assets/img/mawgiflogo.png";

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const InputField = ({
  id,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  onKeyDown,
  error,
  icon: Icon,
  rightSlot,
  autoComplete,
}) => (
  <div className="space-y-1">
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border bg-white transition-all duration-200 ${
        error
          ? "border-red-400 ring-1 ring-red-300"
          : "border-slate-200 focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-200"
      }`}
    >
      {Icon && <Icon className="w-4 h-4 text-cyan-600 shrink-0" />}
      <input
        id={id}
        name={name}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="login-input flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none min-w-0"
      />
      {rightSlot}
    </div>
    {error && (
      <motion.p
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xs text-red-500 pl-1"
      >
        {error}
      </motion.p>
    )}
  </div>
);

// ── Particle Canvas Component ──────────────────────────────────────────────────
const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createParticle = () => ({
      x: Math.random() * canvas.width,
      y: canvas.height + 5,
      r: Math.random() * 2.5 + 0.8,
      vx: (Math.random() - 0.5) * 0.35,
      vy: -(Math.random() * 0.5 + 0.15),
      alpha: Math.random() * 0.45 + 0.15,
      life: 0,
      maxLife: Math.random() * 220 + 120,
    });

    const init = () => {
      resize();
      particles = Array.from({ length: 55 }, () => {
        const p = createParticle();
        p.y = Math.random() * canvas.height; // scatter initially
        return p;
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.life++;
        p.x += p.vx;
        p.y += p.vy;

        const progress = p.life / p.maxLife;
        const fade =
          progress < 0.15
            ? progress / 0.15
            : progress > 0.75
            ? 1 - (progress - 0.75) / 0.25
            : 1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(14,165,233,${p.alpha * fade})`;
        ctx.fill();

        if (
          p.life >= p.maxLife ||
          p.y < -10 ||
          p.x < -10 ||
          p.x > canvas.width + 10
        ) {
          particles[i] = createParticle();
        }
      }

      // Draw connecting lines between nearby particles
      ctx.strokeStyle = "rgba(14,165,233,0.06)";
      ctx.lineWidth = 0.6;
      for (let a = 0; a < particles.length; a++) {
        for (let b = a + 1; b < particles.length; b++) {
          const dx = particles[a].x - particles[b].x;
          const dy = particles[a].y - particles[b].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 75) {
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    init();
    draw();

    const handleResize = () => resize();
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
};

// ── Pulse Rings Component ──────────────────────────────────────────────────────
const PulseRings = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="absolute rounded-full border border-cyan-400/20"
        style={{
          animation: `pulseRing 3.5s ease-out infinite`,
          animationDelay: `${i * 1.15}s`,
        }}
      />
    ))}
    {/* Center dot */}
    <span
      className="absolute w-3 h-3 rounded-full bg-cyan-400/30"
      style={{ animation: "dotPulse 3.5s ease-in-out infinite" }}
    />

    <style>{`
      @keyframes pulseRing {
        0%   { width: 60px;  height: 60px;  opacity: 0.7; }
        100% { width: 380px; height: 380px; opacity: 0;   }
      }
      @keyframes dotPulse {
        0%, 100% { transform: scale(1);   opacity: 0.4; }
        50%       { transform: scale(1.5); opacity: 0.9; }
      }
    `}</style>
  </div>
);

// ── Main LoginPage ─────────────────────────────────────────────────────────────
const LoginPage = () => {
  const [formData, setFormData] = useState(() => ({
    email: getRememberedEmail(),
    password: "",
  }));
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(isRememberMeEnabled);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { triggerPermissionLoad } = usePermissions();

  useEffect(() => {
 const token = getAccessToken();
  // const expiryTime = localStorage.getItem("token_expiry");
  // const currentTime = Date.now();
//  if (token || expiryTime || currentTime < Number(expiryTime)) {
 if (token) {
      if (navigate) {
        navigate("/", { replace: true });
      } else {
        window.location.href = "/";
      }
      return;
    }
  }, []);

  // Restore the saved password from the browser's password manager (where supported)
  useEffect(() => {
    let active = true;
    getRememberedPassword(getRememberedEmail()).then((password) => {
      if (active && password) {
        setFormData((prev) => (prev.password ? prev : { ...prev, password }));
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const handleRememberChange = (e) => {
    const { checked } = e.target;
    setRememberMe(checked);
    setRememberPreference(checked);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await schema.validate(formData, { abortEarly: false });
      const response = await apiCall("POST", "/WPUser/login", formData, navigate);
      if (response?.success && response?.errorCode === 0) {
        // const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
        startSession(rememberMe, response.access_token, formData);
        // localStorage.setItem("token_expiry", expiryTime.toString());
        localStorage.setItem("user", JSON.stringify(response?.data) || null);
        await triggerPermissionLoad();
        navigate("/");
      } else {
        clearSession();
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors = {};
        error.inner.forEach((err) => (newErrors[err.path] = err.message));
        setErrors(newErrors);
      } else {
        console.log(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-screen flex overflow-hidden">

      {/* ══ LEFT PANEL ══ */}
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="hidden md:block md:w-1/2 shrink-0 min-h-screen relative"
      >
        <img
          src={CbPortal}
          alt="Crystal Ball"
            fetchpriority="high"
          className="w-full h-full object-cover absolute inset-0"
        />
        <div className="absolute inset-0 bg-black/10" />

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl flex flex-col items-center justify-center p-8"
          style={{
            width: "75%",
            maxWidth: "380px",
            minHeight: "420px",
            background: "rgba(255,255,255,0.15)",
            border: "1.5px solid rgba(255,255,255,0.35)",
            // backdropFilter: "blur(12px)",
            // WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5, ease: "easeOut" }}
            src={mawgiflogo}
            alt="Crystal Ball Logo"
            className="w-4/5 h-auto object-contain mb-4"
          />
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.4, ease: "easeOut" }}
            className="text-white text-1xl font-bold tracking-wide text-center"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
          >
            Crystal Ball
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.4 }}
          className="absolute bottom-4 left-0 right-0 text-center px-4"
        >
          <p className="text-white text-xs font-medium opacity-70">
            Secured by Crystal Ball Portal © 2026
          </p>
        </motion.div>
      </motion.div>

      {/* ══ RIGHT PANEL ══ */}
      <div
  className="w-full md:w-1/2 flex flex-col items-center justify-center px-6 sm:px-10 lg:px-16 py-12 relative"
  style={{
    overflow: "hidden",   // <-- add karo
    maxHeight: "100vh",   // <-- add karo
    height: "100vh",      // <-- add karo
  }}
>
        {/* ── Particle Canvas ── */}
        <ParticleCanvas />

        {/* ── Pulse Rings ── */}
        <PulseRings />

        {/* ── Blobs ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="absolute rounded-full pointer-events-none"
          style={{ width: 100, height: 100, top: 30, left: 5, background: "rgba(228,244,253,0.55)" }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="absolute rounded-full pointer-events-none"
          style={{ width: 170, height: 170, top: -30, right: -40, background: "rgba(228,244,253,0.55)" }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="absolute rounded-full pointer-events-none"
          style={{ width: 80, height: 80, bottom: 40, right: 30, background: "rgba(228,244,253,0.55)" }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="absolute pointer-events-none"
          style={{ width: 80, height: 80, bottom: 30, left: 30, borderRadius: 12, background: "rgba(228,244,253,0.55)" }}
        />

        {/* Mobile logo */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex md:hidden mb-6"
        >
          <img
            src={mawgiflogo}
            alt="Mawgif Crystal Ball Logo"
            className="w-28 h-auto object-contain"
          />
        </motion.div>

        <div className="w-full max-w-sm relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
            className="mb-8"
          >
            <h1
              className="text-transparent bg-clip-text text-3xl font-bold"
              style={{ backgroundImage: "linear-gradient(90deg, #0ea5e9, #06b6d4)" }}
            >
              Welcome{" "}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #0ea5e9, #06b6d4)" }}
              >
                Back
              </span>
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Sign in to your Crystal Ball Portal account
            </p>
          </motion.div>

          <div className="space-y-4">

            {/* Email Field */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.45, ease: "easeOut" }}
            >
              <InputField
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                error={errors.email}
                icon={Mail}
              />
            </motion.div>

            {/* Password Field */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45, ease: "easeOut" }}
            >
              <InputField
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                error={errors.password}
                icon={Lock}
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-slate-400 hover:text-slate-600 transition-colors shrink-0"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                }
              />
            </motion.div>

            {/* Remember Me */}
            <motion.label
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.4, ease: "easeOut" }}
              className="flex items-center gap-2 cursor-pointer select-none w-fit"
            >
              <input
                type="checkbox"
                name="rememberMe"
                checked={rememberMe}
                onChange={handleRememberChange}
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className={`w-4 h-4 rounded border flex items-center justify-center transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-400 peer-focus-visible:ring-offset-1 ${
                  rememberMe
                    ? "bg-cyan-500 border-cyan-500"
                    : "border-slate-300 bg-white"
                }`}
              >
                {rememberMe && (
                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M1.5 5.5L3.8 8l4.7-6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <span className="text-sm text-slate-500">Remember me</span>
            </motion.label>

            {/* Submit Button — shimmer effect added */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.4, ease: "easeOut" }}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-semibold text-sm shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 relative overflow-hidden"
                style={{
                  background: "linear-gradient(90deg, #0ea5e9 0%, #06b6d4 100%)",
                }}
              >
                {/* Shimmer sweep */}
                {!isLoading && (
                  <span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.28) 50%, transparent 60%)",
                      backgroundSize: "200% 100%",
                      animation: "btnShimmer 2.6s ease-in-out infinite",
                    }}
                  />
                )}

                {isLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                    />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ChevronsRight  className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </motion.div>

          </div>
        </div>
      </div>

      {/* Global keyframes for shimmer + pulse (injected once) */}
      <style>{`
        @keyframes btnShimmer {
          0%        { background-position: 200% center; }
          60%, 100% { background-position: -200% center; }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;